/* eslint-disable @typescript-eslint/member-ordering */
import { ChangeDetectorRef, Component, computed, effect, input, OnInit, output, signal } from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import DecoupledEditor from '@ckeditor/ckeditor5-build-decoupled-document';
import { Editor } from '@ckeditor/ckeditor5-core';
import { faCode } from '@fortawesome/pro-regular-svg-icons';

@Component({
  selector: 'eiq-text-editor',
  templateUrl: 'text-editor.component.html',
  styleUrls: ['text-editor.component.scss']
})
export class TextEditorComponent implements OnInit {
  allowLineBreaks = input<boolean>();
  appearance = input<'input' | 'textarea' | 'essay'>('input');
  autofocus = input<boolean>();
  control = input<AbstractControl>();
  disabled = input<boolean>(false);
  hideToolbar = input<boolean>(false);
  id = input.required<string>();
  placeholder = input<string>('');
  showBorder = input<boolean>(true);
  stopDefaultTab = input<boolean>();
  testId = input<string>();

  tab = output<void>();
  blur = output<void>();

  editorCtrl$ = computed(() => this.control() as FormControl);

  focused$ = signal<boolean>(false);

  private readonly _disabledEffect = effect(() => {
    const ctrl = this.editorCtrl$();
    if (!ctrl) {
      return;
    }
    if (this.disabled()) {
      ctrl.disable({ emitEvent: false });
    } else {
      ctrl.enable({ emitEvent: false });
    }
  });

  onChange: (value: string) => void;
  onTouched: () => void;

  config: { placeholder: string };
  editor: any = DecoupledEditor;

  editorInstance: any;
  faCode = faCode;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.config = {
      placeholder: this.placeholder()
    };

    DecoupledEditor.defaultConfig = {
      toolbar: {
        items: ['|', 'bold', 'italic', 'underline', '|', 'bulletedList', 'numberedList', '|', 'undo', 'redo']
      },
      image: {
        resizeUnit: 'px',
        toolbar: []
      },
      table: {
        contentToolbar: []
      },
      list: { properties: { styles: true, reversed: true, startIndex: true } },
      language: 'en'
    };
  }

  onReady(editor: Editor): void {
    this.editorInstance = editor;

    if (!this.hideToolbar()) {
      const toolbarContainer = document.querySelector(`.eiq-text-editor__toolbar_${this.id()}`);
      toolbarContainer.appendChild(editor.ui.view.toolbar.element);
    }

    editor.ui.focusTracker.on('change:isFocused', (_evt, _name, value: boolean) => {
      if (!value) {
        this.blur.emit();
      }

      this.focused$.update(() => value);
      this.cdr.detectChanges();
    });

    editor.keystrokes.set('tab', (data: KeyboardEvent, stop: Function) => {
      this.tab.emit();
      if (this.stopDefaultTab()) {
        stop();
      }
    });

    editor.keystrokes.set('enter', (data: KeyboardEvent, stop: Function) => {
      this.tab.emit();
      if (!this.allowLineBreaks()) {
        stop();
      }
    });

    if (this.autofocus()) {
      queueMicrotask(() => {
        editor.editing.view.focus();

        editor.model.change((writer) => {
          writer.setSelection(writer.createPositionAt(editor.model.document.getRoot(), 'end'));
        });
      });
    }
  }
}
